// BookingReserved: Page de confirmation pour réservation sans paiement
import React from "react";
import { useLocation, Link } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";

export default function BookingReserved() {
  const location = useLocation();
  const { booking, tour, customerName, email, phone } = location.state || {};
  const { settings } = useSettings();
  const waPhone = (settings.company_whatsapp || "+212771373922").replace(/[^0-9]/g, "");
  const currencySymbol = settings.currency_symbol || "$";

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      {/* Success Icon */}
      <div className="mb-6 flex justify-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
          <svg
            className="h-10 w-10 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      </div>

      {/* Title */}
      <h1 className="mb-2 text-center text-3xl font-bold text-slate-900 dark:text-slate-100">
        Reservation Received!
      </h1>
      <p className="mb-8 text-center text-slate-600 dark:text-slate-400">
        Thank you for your interest. Our team will contact you shortly to confirm your booking.
      </p>

      {/* Info Card */}
      <div className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {/* What happens next */}
        <div className="mb-6">
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            What happens next?
          </h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600 dark:bg-blue-900/30">
                1
              </div>
              <div>
                <div className="font-medium text-slate-900 dark:text-slate-100">
                  We'll call you within 24 hours
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Our team will reach out to confirm your details and answer any questions.
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600 dark:bg-blue-900/30">
                2
              </div>
              <div>
                <div className="font-medium text-slate-900 dark:text-slate-100">
                  Confirm & arrange payment
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Once confirmed, you can pay via bank transfer, cash, or card over the phone.
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600 dark:bg-blue-900/30">
                3
              </div>
              <div>
                <div className="font-medium text-slate-900 dark:text-slate-100">
                  Receive confirmation email
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  You'll receive a booking confirmation with all the details.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reservation Details */}
        {(customerName || tour) && (
          <div className="border-t border-slate-200 pt-6 dark:border-slate-700">
            <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
              Your Reservation Details
            </h3>
            <div className="space-y-3 text-sm">
              {customerName && (
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Name</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {customerName}
                  </span>
                </div>
              )}
              {email && (
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Email</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {email}
                  </span>
                </div>
              )}
              {phone && (
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Phone</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {phone}
                  </span>
                </div>
              )}
              {tour?.title && (
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Tour</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {tour.title}
                  </span>
                </div>
              )}
              {booking?.booking_date && (
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Date</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {new Date(booking.booking_date).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
              )}
              {booking?.number_of_people && (
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Travelers</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {booking.number_of_people} {booking.number_of_people > 1 ? "people" : "person"}
                  </span>
                </div>
              )}
              {booking?.total_price && (
                <div className="flex justify-between border-t border-slate-200 pt-3 dark:border-slate-700">
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    Total Price
                  </span>
                  <span className="text-xl font-bold text-emerald-600">
                    {currencySymbol}{booking.total_price}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contact Info */}
      <div className="mb-8 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
        <div className="flex items-start gap-3">
          <svg
            className="h-5 w-5 flex-shrink-0 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <div>
            <div className="font-medium text-blue-900 dark:text-blue-100">
              Questions? Contact us directly
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">
              WhatsApp: <a href={`https://wa.me/${waPhone}`} className="underline hover:no-underline">{settings.company_whatsapp}</a> | Email: <a href={`mailto:${settings.company_email}`} className="underline hover:no-underline">{settings.company_email}</a>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
        <Link
          to="/tours"
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Browse More Tours
        </Link>
        <Link
          to="/"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-emerald-700"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          Back to Home
        </Link>
      </div>
    </div>
  );
}
